import React, { useMemo } from "react";
import { useTransactionTrackingStore } from "../../store/useTransactionTrackingStore";
import {
  useActiveTransactions,
  useFeatureFlagsByCategory,
} from "../../hooks/query/queries";
import { useTransactionSocket } from "../../hooks/useTransactionSocket";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";

export default function LiveTransactionIndicator({ hideLabel = false }) {
  const { isAuthenticated } = useAuth();
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures");
  const isEnabled = isAuthenticated && featureFlags?.liveTransactionTracking;

  // Fetch active transactions and enable WebSocket for real-time updates
  useActiveTransactions({ enabled: isEnabled });
  useTransactionSocket(isEnabled);

  const transactions = useTransactionTrackingStore((s) => s.activeTransactions);

  // Get the most recent active (non-completed) transaction
  const activeTransaction = useMemo(
    () =>
      transactions
        ?.filter(
          (tx) => !["Completed", "Failed", "Rejected"].includes(tx.status)
        )
        ?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))?.[0],
    [transactions]
  );

  if (!isEnabled || !activeTransaction) {
    return null;
  }

  const isDeposit =
    activeTransaction.transactionType === "receive" ||
    activeTransaction.transactionType === "deposit";
  const label = isDeposit ? "New Deposit" : "New Withdraw";
  const hasWarning = activeTransaction.statusUnavailable;

  return (
    <Link
      to="/trading/digitalWallet"
      state={{ parentNavTab: "overview" }}
      className={`flex items-center rounded-full border border-[#EBE9EF] hover:bg-[#F5F4F7] transition-colors ${
        hideLabel ? "p-2" : "gap-3 px-4 py-2"
      }`}
    >
      {/* Indicator icon with smooth breathing animation */}
      <style>
        {`
          @keyframes breathe {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.3); opacity: 0.75; }
          }
        `}
      </style>
      <div className="relative w-6 h-6">
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ animation: "breathe 2s ease-in-out infinite" }}
        >
          {/* Double ring path */}
          <path
            d="M12 2C6.47717 2 2 6.47717 2 12C2 17.5228 6.47717 22 12 22C17.5201 21.9935 21.9935 17.5201 22 12C22 6.47717 17.5228 2 12 2ZM12 21C7.02942 21 3 16.9706 3 12C3 7.02942 7.02942 3 12 3C16.9683 3.00543 20.9946 7.03168 21 12C21 16.9706 16.9706 21 12 21ZM12 6C8.68628 6 6 8.68628 6 12C6 15.3137 8.68628 18 12 18C15.3122 17.9964 17.9964 15.3122 18 12C18 8.68628 15.3137 6 12 6ZM12 17C9.23859 17 7 14.7614 7 12C7 9.23859 9.23859 7 12 7C14.7601 7.00323 16.9968 9.23993 17 12C17 14.7614 14.7614 17 12 17Z"
            fill={hasWarning ? "#FF9500" : "#17C3A5"}
          />
          {/* Center filled circle */}
          <circle
            cx="12"
            cy="12"
            r="5"
            fill={hasWarning ? "#FF9500" : "#17C3A5"}
          />
        </svg>
      </div>

      {/* Label */}
      {!hideLabel && (
        <span className="text-[14px] font-semibold text-[#423E51] hidden md:inline">
          {label}
        </span>
      )}
    </Link>
  );
}
